import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import NavigationBreadcrumbs from '../../components/navigation/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MetricCard from './components/MetricCard';
import CampaignCard from './components/CampaignCard';
import ConversionChart from './components/ConversionChart';
import CustomerSegmentChart from './components/CustomerSegmentChart';
import ProductCatalogUpload from './components/ProductCatalogUpload';
import CreateCampaignModal from './components/CreateCampaignModal';
import PerformanceReportTable from './components/PerformanceReportTable';


import ARModeViewer from '../../components/ARModeViewer';
import { useAuth } from '../../contexts/AuthContext';
import { partnersService } from '../../services/financialDataService';
import { supabase } from '../../lib/supabase';

const RetailPartnerDashboard = () => {
  const { user } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30days');
  const [chartType, setChartType] = useState('line');
  const [showARMode, setShowARMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const timeRangeOptions = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'year', label: 'This Year' }];


  const [metricsData, setMetricsData] = useState([
  {
    icon: 'TrendingUp',
    title: 'Total Revenue',
    value: '$0',
    change: '+0%',
    changeType: 'positive',
    trend: true,
    color: 'var(--color-primary)'
  },
  {
    icon: 'ShoppingCart',
    title: 'Total Conversions',
    value: '0',
    change: '+0%',
    changeType: 'positive',
    trend: true,
    color: 'var(--color-accent)'
  },
  {
    icon: 'Target',
    title: 'Conversion Rate',
    value: '0%',
    change: '+0%',
    changeType: 'positive',
    trend: true,
    color: 'var(--color-secondary)'
  },
  {
    icon: 'DollarSign',
    title: 'Average Order Value',
    value: '$0',
    change: '+0%',
    changeType: 'positive',
    trend: true,
    color: 'var(--color-success)'
  },
  {
    icon: 'Users',
    title: 'Active Customers',
    value: '0',
    change: '+0%',
    changeType: 'positive',
    trend: true,
    color: 'var(--color-warning)'
  },
  {
    icon: 'BarChart3',
    title: 'Campaign ROI',
    value: '0%',
    change: '+0%',
    changeType: 'positive',
    trend: true,
    color: 'var(--color-primary)'
  }
]);


  const [campaignsData, setCampaignsData] = useState([]);
  const [partnerData, setPartnerData] = useState(null);

  // Fetch partner data from Supabase
  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch partner profile
        const partner = await partnersService?.get(user?.id);
        setPartnerData(partner);

        if (partner) {
          // Update metrics from partner data
          setMetricsData([
            {
              icon: 'TrendingUp',
              title: 'Total Revenue',
              value: `$${parseFloat(partner?.totalRevenue || 0)?.toLocaleString()}`,
              change: '+18.2%',
              changeType: 'positive',
              trend: true,
              color: 'var(--color-primary)'
            },
            {
              icon: 'ShoppingCart',
              title: 'Total Conversions',
              value: (partner?.totalConversions || 0)?.toLocaleString(),
              change: '+12.5%',
              changeType: 'positive',
              trend: true,
              color: 'var(--color-accent)'
            },
            {
              icon: 'Target',
              title: 'Conversion Rate',
              value: `${parseFloat(partner?.conversionRate || 0)?.toFixed(1)}%`,
              change: '+0.8%',
              changeType: 'positive',
              trend: true,
              color: 'var(--color-secondary)'
            },
            {
              icon: 'DollarSign',
              title: 'Average Order Value',
              value: partner?.totalConversions > 0 ? `$${(partner?.totalRevenue / partner?.totalConversions)?.toFixed(2)}` : '$0',
              change: '+5.2%',
              changeType: 'positive',
              trend: true,
              color: 'var(--color-success)'
            },
            {
              icon: 'Users',
              title: 'Active Customers',
              value: (partner?.activeCustomers || 0)?.toLocaleString(),
              change: '+24.1%',
              changeType: 'positive',
              trend: true,
              color: 'var(--color-warning)'
            },
            {
              icon: 'BarChart3',
              title: 'Campaign ROI',
              value: '342%',
              change: '+28.3%',
              changeType: 'positive',
              trend: true,
              color: 'var(--color-primary)'
            }
          ]);

          // Fetch campaigns
          const campaigns = await partnersService?.getCampaigns(partner?.id);
          const transformedCampaigns = campaigns?.map(campaign => ({
            id: campaign?.id,
            name: campaign?.name,
            description: campaign?.description,
            image: campaign?.imageUrl || 'https://images.unsplash.com/photo-1610901005940-f841bdef4bc7',
            imageAlt: campaign?.name,
            status: campaign?.status,
            discount: campaign?.discountPercentage ? `${campaign?.discountPercentage}% Off` : 'Special Offer',
            conversions: (campaign?.conversions || 0)?.toLocaleString(),
            revenue: `$${parseFloat(campaign?.revenue || 0)?.toLocaleString()}`,
            roi: `${parseFloat(campaign?.roi || 0)?.toFixed(0)}%`,
            startDate: campaign?.startDate,
            endDate: campaign?.endDate
          }));
          setCampaignsData(transformedCampaigns);
        }
      } catch (error) {
        console.error('Error fetching partner data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Real-time subscriptions
    const partnersChannel = supabase?.channel('partners_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'retail_partners',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const campaignsChannel = supabase?.channel('campaigns_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'campaigns'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(partnersChannel);
      supabase?.removeChannel(campaignsChannel);
    };
  }, [user?.id]);


  const conversionChartData = [
  { date: 'Jan 10', conversions: 145, revenue: 10850 },
  { date: 'Jan 12', conversions: 178, revenue: 13240 },
  { date: 'Jan 14', conversions: 192, revenue: 14280 },
  { date: 'Jan 16', conversions: 165, revenue: 12320 },
  { date: 'Jan 18', conversions: 210, revenue: 15680 },
  { date: 'Jan 20', conversions: 234, revenue: 17450 },
  { date: 'Jan 22', conversions: 198, revenue: 14760 },
  { date: 'Jan 24', conversions: 256, revenue: 19120 },
  { date: 'Jan 26', conversions: 289, revenue: 21580 },
  { date: 'Jan 28', conversions: 312, revenue: 23340 },
  { date: 'Jan 30', conversions: 278, revenue: 20760 },
  { date: 'Feb 01', conversions: 345, revenue: 25780 }];


  const customerSegmentData = [
  { name: 'High Spenders', value: 2847, percentage: 22.8 },
  { name: 'Budget Conscious', value: 4156, percentage: 33.4 },
  { name: 'New Customers', value: 1892, percentage: 15.2 },
  { name: 'Loyal Customers', value: 2345, percentage: 18.8 },
  { name: 'Occasional Buyers', value: 1218, percentage: 9.8 }];


  const performanceReports = [
  {
    date: 'Feb 15, 2026',
    campaign: 'Summer Savings Bonanza',
    impressions: '45,892',
    conversions: '1,247',
    revenue: '$92,450',
    roi: '+385%'
  },
  {
    date: 'Feb 14, 2026',
    campaign: 'New Customer Welcome Offer',
    impressions: '38,456',
    conversions: '892',
    revenue: '$67,320',
    roi: '+298%'
  },
  {
    date: 'Feb 13, 2026',
    campaign: 'Summer Savings Bonanza',
    impressions: '42,178',
    conversions: '1,134',
    revenue: '$84,230',
    roi: '+372%'
  },
  {
    date: 'Feb 12, 2026',
    campaign: 'New Customer Welcome Offer',
    impressions: '35,892',
    conversions: '823',
    revenue: '$62,180',
    roi: '+285%'
  },
  {
    date: 'Feb 11, 2026',
    campaign: 'Holiday Financial Planning',
    impressions: '51,234',
    conversions: '1,456',
    revenue: '$108,920',
    roi: '+425%'
  }];


  const handleUploadCatalog = (data) => {
    console.log('Uploading catalog:', data);
    setShowUploadModal(false);
  };

  const handleCreateCampaign = (data) => {
    console.log('Creating campaign:', data);
    setShowCampaignModal(false);
  };

  const handleEditCampaign = (campaign) => {
    console.log('Editing campaign:', campaign);
  };

  const handleViewAnalytics = (campaign) => {
    console.log('Viewing analytics for:', campaign);
  };

  const partnerARData = {
    totalRevenue: 284592,
    totalConversions: 3847,
    conversionRate: 4.8,
    avgOrderValue: 73.95,
    activeCustomers: 12458,
    campaignROI: 342
  };

  return (
    <>
      <Helmet>
        <title>Retail Partner Dashboard - FINVISAR</title>
        <meta name="description" content="Manage campaigns, analyze consumer behavior, and optimize financial incentive strategies with comprehensive merchant analytics" />
      </Helmet>
      <RoleBasedNavigation />
      <div className="min-h-screen bg-gradient-to-br from-fintech-teal-50 via-white to-fintech-green-50 pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <NavigationBreadcrumbs />

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mt-6 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-fintech-teal-600 to-fintech-green-600 bg-clip-text text-transparent mb-2">
                Partner Dashboard
              </h1>
              <p className="text-muted-foreground">
                Monitor campaign performance and consumer insights
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setShowUploadModal(true)}
                iconName="Upload"
                iconPosition="left"
                variant="outline"
                className="border-fintech-teal-300 hover:bg-fintech-teal-50 hover:border-fintech-teal-500"
              >
                Upload Catalog
              </Button>
              <Button
                onClick={() => setShowCampaignModal(true)}
                iconName="Plus"
                iconPosition="left"
                className="bg-gradient-to-r from-fintech-teal-500 to-fintech-green-500 hover:from-fintech-teal-600 hover:to-fintech-green-600 shadow-glow-teal"
              >
                Create Campaign
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {metricsData?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          {/* AR Feature Showcase for Partners */}
          <div className="mb-6 bg-gradient-to-br from-fintech-teal-500 via-fintech-green-500 to-fintech-blue-500 rounded-3xl p-8 shadow-elevation-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-fintech-purple-500/20 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center animate-float">
                    <Icon name="Camera" size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-white">AR Consumer Analytics</h2>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full border border-white/30">PARTNER</span>
                    </div>
                    <p className="text-white/90 text-sm">Visualize consumer behavior and product performance in real-time</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowARMode(true)}
                  className="px-6 py-3 bg-white text-fintech-teal-600 rounded-xl font-bold shadow-elevation-3 hover:shadow-elevation-5 hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <Icon name="Camera" size={20} />
                  Launch AR Analytics
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Eye" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">Behavior Mapping</h3>
                  </div>
                  <p className="text-white/80 text-sm">AR visualization of customer purchase patterns</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="BarChart3" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">Shelf Analytics</h3>
                  </div>
                  <p className="text-white/80 text-sm">Real-time product performance heat mapping</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="Target" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">Campaign Overlay</h3>
                  </div>
                  <p className="text-white/80 text-sm">AR-powered campaign performance visualization</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="TrendingUp" size={20} className="text-white" />
                    <h3 className="text-white font-semibold">ROI Insights</h3>
                  </div>
                  <p className="text-white/80 text-sm">Instant AR-based return on investment analysis</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-elevation-3 border border-fintech-teal-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Conversion Tracking</h2>
                  <p className="text-xs md:text-sm text-muted-foreground">Monitor campaign performance and revenue trends</p>
                </div>
                <div className="flex gap-2">
                  <Select
                    options={timeRangeOptions}
                    value={selectedTimeRange}
                    onChange={setSelectedTimeRange}
                    className="w-full sm:w-auto" />
                  
                  <Button
                    variant={chartType === 'line' ? 'default' : 'outline'}
                    size="sm"
                    iconName="LineChart"
                    onClick={() => setChartType('line')} />
                  
                  <Button
                    variant={chartType === 'area' ? 'default' : 'outline'}
                    size="sm"
                    iconName="AreaChart"
                    onClick={() => setChartType('area')} />
                  
                </div>
              </div>
              <ConversionChart data={conversionChartData} type={chartType} />
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-elevation-2 border border-fintech-accent-200">
              <div className="mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Customer Segmentation</h2>
                <p className="text-xs md:text-sm text-muted-foreground">Distribution of customer types</p>
              </div>
              <CustomerSegmentChart data={customerSegmentData} />
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Active Campaigns</h2>
                <p className="text-xs md:text-sm text-muted-foreground">Manage and monitor your marketing campaigns</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {campaignsData?.map((campaign) =>
              <CampaignCard
                key={campaign?.id}
                campaign={campaign}
                onEdit={handleEditCampaign}
                onViewAnalytics={handleViewAnalytics} />

              )}
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-elevation-2 border border-fintech-accent-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Performance Reports</h2>
                <p className="text-xs md:text-sm text-muted-foreground">Detailed analytics on customer acquisition and revenue impact</p>
              </div>
              <Button variant="outline" iconName="Download" iconPosition="left">
                Export All Reports
              </Button>
            </div>
            <PerformanceReportTable reports={performanceReports} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-6 md:mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-elevation-2 border border-fintech-teal-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Zap" size={20} color="var(--color-primary)" className="md:w-6 md:h-6" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground">Quick Actions</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-smooth text-left">
                  <Icon name="FileText" size={18} />
                  <span className="text-sm text-foreground">Generate Monthly Report</span>
                </button>
                <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-smooth text-left">
                  <Icon name="Settings" size={18} />
                  <span className="text-sm text-foreground">Configure Discount Rules</span>
                </button>
                <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-smooth text-left">
                  <Icon name="Users" size={18} />
                  <span className="text-sm text-foreground">View Customer Insights</span>
                </button>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-elevation-2 border border-fintech-accent-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="TrendingUp" size={20} color="var(--color-accent)" className="md:w-6 md:h-6" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground">Top Performing Products</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Premium Savings Account</span>
                  <span className="text-sm font-semibold text-success">$45,230</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Investment Portfolio Plus</span>
                  <span className="text-sm font-semibold text-success">$38,920</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Credit Builder Card</span>
                  <span className="text-sm font-semibold text-success">$32,450</span>
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-elevation-2 border border-fintech-warning-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Icon name="Bell" size={20} color="var(--color-warning)" className="md:w-6 md:h-6" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground">Recent Notifications</h3>
              </div>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-foreground">Campaign approved</p>
                    <p className="text-xs text-muted-foreground">2 hours ago</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Icon name="AlertCircle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-foreground">Budget threshold reached</p>
                    <p className="text-xs text-muted-foreground">5 hours ago</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-foreground">New analytics available</p>
                    <p className="text-xs text-muted-foreground">1 day ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showUploadModal &&
      <ProductCatalogUpload
        onUpload={handleUploadCatalog}
        onClose={() => setShowUploadModal(false)} />

      }
      {showCampaignModal &&
      <CreateCampaignModal
        onClose={() => setShowCampaignModal(false)}
        onSave={handleCreateCampaign} />

      }
      {showARMode && (
        <ARModeViewer
          budgetData={[]}
          financialData={partnerARData}
          onClose={() => setShowARMode(false)}
          userRole="partner"
        />
      )}
    </>
  );

};

export default RetailPartnerDashboard;